import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, FileText, Shield, Scale, AlertCircle } from 'lucide-react';
import axios from 'axios';

// ============================================================
// ALDRIN'S RESTAURANT - CONDITIONS GÉNÉRALES DE VENTE
// Design: Professionnel, structuré, juridique
// ============================================================

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_resto-stock-manager-3/artifacts/0l7b747s_9F91CBF8-DC1E-49E7-9C24-96206851AEA7.PNG';
const BRAND_GREEN = '#1a3d2c';

// Contenu par défaut des CGV
const DEFAULT_TERMS_CONTENT = `Les présentes conditions générales de vente et d'utilisation (ci-après les « Conditions ») ont pour objet de définir les modalités d'accès, de fréquentation et de consommation au sein de l'établissement Aldrin's Restaurant.

Toute personne accédant à l'établissement, procédant à une réservation ou consommant un service reconnaît avoir pris connaissance des présentes Conditions et les accepter sans réserve.

---

1. Identification de l'établissement
Aldrin's Restaurant est un établissement de restauration offrant des prestations de vente de produits alimentaires et de services associés, dans un cadre professionnel structuré.

---

2. Champ d'application
Les présentes Conditions s'appliquent à l'ensemble des prestations proposées par Aldrin's Restaurant, sans exception.
Elles prévalent sur tout autre document ou communication.

---

3. Formation de la vente
Toute commande passée au sein de l'établissement constitue un engagement ferme et définitif du client.
La vente est réputée formée dès validation de la commande.

---

4. Conditions de paiement
Les prestations sont payables immédiatement, au moment de la commande ou selon les modalités en vigueur au sein de l'établissement.
Aucun crédit, délai de paiement ou facilité de règlement n'est accordé.
Toute consommation est intégralement due.

---

5. Organisation du service
Le service est exclusivement assuré dans l'enceinte de l'établissement ou dans les espaces expressément prévus à cet effet.
Il est strictement interdit au personnel de sortir de l'établissement pour effectuer un service à l'extérieur, notamment sur la voie publique.

---

6. Obligations du client
Le client s'engage à adopter un comportement conforme aux règles élémentaires de respect, de civilité et d'hygiène.

À ce titre, il lui incombe notamment :
• de respecter le personnel de l'établissement
• de respecter les autres clients
• de préserver l'intégrité des installations, équipements et sanitaires
• d'adopter un comportement compatible avec un environnement professionnel

Toute dégradation, détérioration ou utilisation abusive des installations pourra donner lieu à réparation, à la charge du client.

---

7. Hygiène et utilisation des installations
Les installations, y compris les sanitaires, sont mises à disposition dans le respect de normes d'hygiène strictes.
Le client s'engage à en faire un usage respectueux et conforme à leur destination.

---

8. Responsabilité de l'établissement
Aldrin's Restaurant décline toute responsabilité en cas de :
• perte, vol ou détérioration d'effets personnels
• incidents résultant de comportements malveillants ou fautifs de tiers

L'établissement ne saurait être tenu responsable des conséquences résultant du comportement des clients.

---

9. Responsabilité du client
Chaque client demeure seul responsable de ses actes, comportements et conséquences qui en découlent au sein de l'établissement.

Toute situation générée par un comportement inapproprié, dangereux ou irrespectueux engage exclusivement la responsabilité de son auteur.

---

10. Refus d'accès et de service
Aldrin's Restaurant se réserve le droit de refuser l'accès ou le service à toute personne dont le comportement serait de nature à porter atteinte :
• à l'ordre public
• à la sécurité
• à l'intégrité du personnel
• au confort des autres clients

Cette décision ne saurait engager la responsabilité de l'établissement.

---

11. Sécurité et maintien de l'ordre
L'établissement se réserve le droit de prendre toute mesure nécessaire afin d'assurer la sécurité des personnes et des biens, ainsi que le bon déroulement du service.

---

12. Modification des conditions
Les présentes Conditions peuvent être modifiées à tout moment, sans préavis, afin de s'adapter à l'évolution de l'activité ou des exigences réglementaires.

---

13. Acceptation
La fréquentation de l'établissement et la consommation des services impliquent l'acceptation pleine et entière des présentes Conditions.

---

Aldrin's Restaurant s'engage, en contrepartie, à fournir un service professionnel, structuré et conforme aux standards de qualité qu'il s'impose.`;

const TermsPage = () => {
    const [content, setContent] = useState(DEFAULT_TERMS_CONTENT);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        const fetchContent = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/client/pages/terms`);
                if (response.data?.content) {
                    setContent(response.data.content);
                }
            } catch (error) {
                console.log('Using default terms content');
            } finally {
                setLoading(false);
            }
        };
        fetchContent();
    }, []);
    
    // Résumé des points clés
    const keyPoints = [
        { icon: FileText, title: 'Vente formée', description: 'Dès validation de commande' },
        { icon: Shield, title: 'Paiement immédiat', description: 'Aucun crédit accordé' },
        { icon: Scale, title: 'Respect mutuel', description: 'Obligations du client' },
        { icon: AlertCircle, title: 'Droit de refus', description: 'Comportement inapproprié' }
    ];
    
    // Fonction pour formater le contenu avec des sections
    const formatContent = (text) => {
        const sections = text.split('---').filter(s => s.trim());
        
        return sections.map((section, index) => {
            const lines = section.trim().split('\n');
            const firstLine = lines[0];
            
            // Check if it's a numbered section (e.g., "1. Titre")
            const isNumberedSection = /^\d+\./.test(firstLine);
            
            if (isNumberedSection) {
                const title = firstLine;
                const content = lines.slice(1).join('\n').trim();
                
                return (
                    <div key={index} className="mb-8">
                        <h3 
                            className="text-lg font-semibold mb-3"
                            style={{ color: BRAND_GREEN }}
                        >
                            {title}
                        </h3>
                        <div className="text-gray-700 leading-relaxed space-y-2">
                            {content.split('\n').map((line, i) => {
                                if (line.startsWith('•')) {
                                    return (
                                        <div key={i} className="flex gap-2 pl-4">
                                            <span style={{ color: BRAND_GREEN }}>•</span>
                                            <span>{line.substring(1).trim()}</span>
                                        </div>
                                    );
                                }
                                return line.trim() ? <p key={i}>{line}</p> : null;
                            })}
                        </div>
                    </div>
                );
            }
            
            // Introduction paragraph
            return (
                <div key={index} className="mb-8">
                    {lines.map((line, i) => (
                        line.trim() ? (
                            <p key={i} className="text-gray-700 leading-relaxed mb-2">
                                {line}
                            </p>
                        ) : null
                    ))}
                </div>
            );
        });
    };
    
    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            {/* Header */}
            <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <Link 
                            to="/client" 
                            className="flex items-center gap-1.5 text-sm transition-colors hover:opacity-70"
                            style={{ color: BRAND_GREEN }}
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Retour
                        </Link>
                        
                        <Link to="/client">
                            <img 
                                src={LOGO_URL} 
                                alt="Aldrin Restaurant" 
                                className="h-10 sm:h-12"
                            />
                        </Link>
                        
                        <div className="w-16" />
                    </div>
                </div>
            </header>
            
            {/* Hero Section */}
            <section 
                className="py-12 sm:py-16 text-center"
                style={{ backgroundColor: `${BRAND_GREEN}05` }}
            >
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-4xl mx-auto px-4"
                >
                    <div 
                        className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
                        style={{ backgroundColor: `${BRAND_GREEN}10` }}
                    >
                        <Scale className="w-8 h-8" style={{ color: BRAND_GREEN }} />
                    </div>
                    <h1 
                        className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3"
                        style={{ color: BRAND_GREEN }}
                    >
                        Conditions Générales de Vente
                    </h1>
                    <p className="text-gray-500 text-sm">
                        Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                    </p>
                </motion.div>
            </section>
            
            {/* Key Points */}
            <section className="py-8 bg-white border-b border-gray-100">
                <div className="max-w-4xl mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {keyPoints.map((point, index) => {
                            const Icon = point.icon;
                            return (
                                <motion.div
                                    key={point.title}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                    className="text-center p-3"
                                >
                                    <Icon 
                                        className="w-5 h-5 mx-auto mb-2" 
                                        style={{ color: BRAND_GREEN }} 
                                    />
                                    <h3 className="font-medium text-gray-800 text-sm">{point.title}</h3>
                                    <p className="text-xs text-gray-500">{point.description}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>
            
            {/* Main Content */}
            <main className="py-10 sm:py-14">
                <motion.article
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="max-w-3xl mx-auto px-4 sm:px-6"
                >
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-10">
                        {loading ? (
                            <div className="animate-pulse space-y-4">
                                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                <div className="h-4 bg-gray-200 rounded w-full"></div>
                                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                            </div>
                        ) : (
                            <div className="legal-content">
                                {formatContent(content)}
                            </div>
                        )}
                    </div>
                    
                    {/* Info box */}
                    <div 
                        className="mt-8 p-5 rounded-xl border"
                        style={{ 
                            backgroundColor: `${BRAND_GREEN}05`,
                            borderColor: `${BRAND_GREEN}20`
                        }}
                    >
                        <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: BRAND_GREEN }} />
                            <div>
                                <p className="font-medium text-gray-800 mb-1">Acceptation des conditions</p>
                                <p className="text-sm text-gray-600">
                                    La fréquentation de l'établissement et la consommation des services 
                                    impliquent l'acceptation pleine et entière des présentes Conditions.
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.article>
            </main>
            
            {/* Footer */}
            <footer className="bg-gray-50 border-t border-gray-100 py-8">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <div className="flex flex-wrap justify-center gap-6 text-sm">
                        <Link to="/client" className="text-gray-500 hover:text-gray-700 transition-colors">
                            Accueil
                        </Link>
                        <Link to="/client/menu" className="text-gray-500 hover:text-gray-700 transition-colors">
                            Notre Carte
                        </Link>
                        <Link to="/client/reservation" className="text-gray-500 hover:text-gray-700 transition-colors">
                            Réservation
                        </Link>
                        <Link to="/client/about" className="text-gray-500 hover:text-gray-700 transition-colors">
                            À propos
                        </Link>
                        <Link to="/client/terms" className="font-medium" style={{ color: BRAND_GREEN }}>
                            CGV
                        </Link>
                    </div>
                    <p className="text-gray-400 text-sm mt-6">
                        © {new Date().getFullYear()} Aldrin's Restaurant. Tous droits réservés.
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default TermsPage;
